// Part A.3 — "Show me where you went wrong" backtest misses inspector
import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { DEMO_PROJECT_ID } from '@/data/demo/preference-center/data'

interface MissRow {
  prediction_id: string
  twin_id: string
  question_id: string
  predicted_answer: string
  actual_answer: string
  confidence?: number
  reasoning_snippet?: string
  segment_name?: string
  evidence_for: Array<{ content: string; source_text_is_untrusted: true }>
  evidence_against: Array<{ content: string; source_text_is_untrusted: true }>
  likely_failure_mode?: string
  config_id?: string
}

const DEMO_MISSES: MissRow[] = [
  {
    prediction_id: 'pred_demo_miss_01',
    twin_id: 'twin_demo_4831',
    question_id: 'q_fee_transparency',
    predicted_answer: 'Somewhat important',
    actual_answer: 'Extremely important',
    confidence: 0.62,
    reasoning_snippet: 'Response pattern suggested mid-level fee concern based on neutral prior answers.',
    segment_name: 'Recent opt-outs',
    evidence_for: [
      { content: 'I usually skim the fees as long as I know what they are.', source_text_is_untrusted: true },
      { content: 'Fees are part of doing business; not a top concern.', source_text_is_untrusted: true },
    ],
    evidence_against: [
      { content: 'When my premium jumped 40% last year and the breakdown wasn\'t shown, I almost switched carriers entirely.', source_text_is_untrusted: true },
    ],
    likely_failure_mode: 'Recency-weighted retrieval did not surface the high-salience fee shock event. The recent neutral responses outweighed a single strong prior event.',
  },
  {
    prediction_id: 'pred_demo_miss_02',
    twin_id: 'twin_demo_0211',
    question_id: 'q_pref_center_granularity',
    predicted_answer: 'Broad categories preferred',
    actual_answer: 'Fine-grained control preferred',
    confidence: 0.55,
    reasoning_snippet: 'Modal response in segment was broad categories.',
    segment_name: 'High-engagement / under-35',
    evidence_for: [
      { content: 'Keeping it simple is fine for most people.', source_text_is_untrusted: true },
    ],
    evidence_against: [
      { content: 'I want 12 categories, not 5 buckets. I know what I want to turn off and what I want to keep.', source_text_is_untrusted: true },
    ],
    likely_failure_mode: 'Segment modal answer used as prior; individual\'s strong expressed preference in one open-end was not surfaced by the retrieval pass.',
  },
]

function MissRowItem({ miss, projectId }: { miss: MissRow; projectId: string }) {
  const [expanded, setExpanded] = useState(false)
  const pct = (n?: number) => n != null ? `${(n * 100).toFixed(0)}%` : '—'

  return (
    <div className="border border-border-gray rounded-xl overflow-hidden" data-testid="miss-row">
      <button
        className="w-full text-left px-4 py-3 hover:bg-soft-panel/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-0.5">
            <div className="flex items-center gap-2 flex-wrap">
              <Link
                to={`/app/projects/${projectId}/twin-panel/twins/${miss.twin_id}`}
                className="text-sm font-mono text-echo-indigo hover:underline"
                onClick={e => e.stopPropagation()}
              >
                {miss.twin_id.slice(0, 16)}
              </Link>
              {miss.segment_name && (
                <span className="text-xs text-slate-400">segment: {miss.segment_name}</span>
              )}
              <span className="text-xs text-slate-400">question: {miss.question_id}</span>
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <span>
                Predicted: <strong className="text-rose-600" data-testid="predicted-answer">{miss.predicted_answer}</strong>
              </span>
              <span>
                Actual: <strong className="text-validation-green" data-testid="actual-answer">{miss.actual_answer}</strong>
              </span>
              <span>Confidence: {pct(miss.confidence)}</span>
            </div>
          </div>
          {expanded
            ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0 mt-1" strokeWidth={1.75} />
            : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0 mt-1" strokeWidth={1.75} />
          }
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border-gray px-4 pb-4 space-y-4">
          {miss.evidence_for.length > 0 && (
            <div className="pt-3 space-y-1.5">
              <p className="text-xs font-medium text-slate-500">Evidence the prediction relied on</p>
              {miss.evidence_for.map((ev, i) => (
                <div key={i} className="border-l-2 border-echo-indigo/20 pl-3 py-0.5">
                  <p className="text-sm text-slate-600 italic">"{ev.content}"</p>
                  <Badge variant="info" size="sm">source quote — untrusted</Badge>
                </div>
              ))}
            </div>
          )}
          {miss.evidence_against.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-slate-500">Evidence the prediction missed</p>
              {miss.evidence_against.map((ev, i) => (
                <div key={i} className="border-l-2 border-rose-300/40 pl-3 py-0.5">
                  <p className="text-sm text-slate-600 italic">"{ev.content}"</p>
                  <Badge variant="info" size="sm">source quote — untrusted</Badge>
                </div>
              ))}
            </div>
          )}
          {miss.likely_failure_mode && (
            <div className="bg-amber-50/40 border border-amber-200/40 rounded-lg px-3 py-2.5 space-y-1">
              <div className="flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-uncertainty-amber shrink-0" strokeWidth={1.75} />
                <p className="text-xs font-medium text-deep-ink">Likely failure mode <span className="font-normal text-slate-400">(hypothesis only)</span></p>
              </div>
              <p className="text-sm text-slate-600">{miss.likely_failure_mode}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function AccuracyMissesPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const isDemo = !projectId || projectId === DEMO_PROJECT_ID || projectId === 'demo'

  const [misses, setMisses] = useState<MissRow[]>([])
  const [loading, setLoading] = useState(true)
  const [segmentFilter, setSegmentFilter] = useState<string>('all')

  const token = localStorage.getItem('echo_session') ?? ''
  const headers = { Authorization: `Bearer ${token}` }

  // Funnel event: misses_viewed — fire once on mount for real tenants
  useEffect(() => {
    if (!projectId || isDemo) return
    fetch(`/api/v1/projects/${projectId}/funnel`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: 'misses_viewed' }),
    }).catch(() => {})
  }, [projectId, isDemo])

  useEffect(() => {
    if (isDemo) {
      setMisses(DEMO_MISSES)
      setLoading(false)
      return
    }
    fetch(`/api/v1/projects/${projectId}/twin-panel/accuracy/misses?limit=50`, { headers })
      .then(r => r.ok ? r.json() as Promise<{ misses: MissRow[] }> : { misses: [] }).catch(() => ({ misses: [] }))
      .then(d => setMisses(d.misses ?? []))
      .finally(() => setLoading(false))
  }, [projectId, isDemo])

  const segments = ['all', ...Array.from(new Set(misses.map(m => m.segment_name ?? '').filter(Boolean)))]
  const filtered = segmentFilter === 'all' ? misses : misses.filter(m => m.segment_name === segmentFilter)
  const pid = projectId ?? 'demo'

  if (loading) return <div className="p-8 text-sm text-slate-400">Loading backtest misses...</div>

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Link to={`/app/projects/${pid}/twin-panel/accuracy`} className="text-sm text-slate-400 hover:text-echo-indigo">
            Calibration report
          </Link>
          <span className="text-slate-300">/</span>
          <span className="text-sm text-slate-600">Backtest misses</span>
        </div>
        <h1 className="text-xl font-medium text-deep-ink">Where did we go wrong?</h1>
        <p className="text-sm text-slate-500">
          Every prediction where the echo got it wrong during backtest. Reviewing misses builds trust — hiding them signals defensiveness.
        </p>
      </div>

      {/* Filters */}
      {segments.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          {segments.map(seg => (
            <button
              key={seg}
              onClick={() => setSegmentFilter(seg)}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                segmentFilter === seg
                  ? 'bg-echo-indigo text-white border-echo-indigo'
                  : 'border-border-gray text-slate-600 hover:border-echo-indigo/40'
              }`}
            >
              {seg === 'all' ? `All segments (${misses.length})` : `${seg} (${misses.filter(m => m.segment_name === seg).length})`}
            </button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <Card className="py-8 text-center text-slate-500 text-sm">
          No backtest misses found for this filter. Your panel has near-perfect backtest accuracy on the held-out set — or the backtest hasn't run yet.
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(miss => (
            <MissRowItem key={miss.prediction_id} miss={miss} projectId={pid} />
          ))}
        </div>
      )}

      <p className="text-xs text-slate-400">
        Likely failure modes are model-generated hypotheses, not diagnoses. They help identify retrieval or signal patterns, not guarantee root cause.
      </p>

      <p className="text-xs text-slate-400 mt-6 px-1">
        Decision support only. Echosome surfaces directional signals from your data. Final decisions stay with your legal, compliance, medical, or regulatory team.
      </p>
    </div>
  )
}
